// ReportsDigestPage — lets Manager (admin) / Super Admin send an ad-hoc
// MIS report email to chosen recipients + CC, for a selected period.

import React, { useState, useEffect } from "react";
import { Mail, Send, AlertCircle, User as UserIcon } from "lucide-react";
import api from "../api/api";
import "../css/reports/misReportModern.css";
import "../css/reports/reportsDigest.css";

const PERIOD_OPTIONS = [
  { value: "7",      label: "Last 7 Days" },
  { value: "30",     label: "Last 30 Days" },
  { value: "90",     label: "Last 90 Days" },
  { value: "month",  label: "This Month" },
  { value: "custom", label: "Custom Range" },
];

const getRange = (period, customFrom, customTo) => {
  if (period === "custom") return { from: customFrom, to: customTo };

  const to = new Date().toISOString().slice(0, 10);
  const days = { "7": 7, "30": 30, "90": 90 }[period];

  if (days) {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return { from: d.toISOString().slice(0, 10), to };
  }

  // "month" — start of current month
  const d = new Date();
  d.setDate(1);
  return { from: d.toISOString().slice(0, 10), to };
};

export default function ReportsDigestPage() {
  const currentRole = localStorage.getItem("role") || "agent";

  const [period,     setPeriod]     = useState("30");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo,   setCustomTo]   = useState("");
  const [sendTo,     setSendTo]     = useState("");
  const [cc,         setCc]         = useState("");
  const [sending,    setSending]    = useState(false);

  const [users,  setUsers]  = useState([]);
  const [userId, setUserId] = useState("");   // "" = all agents

  useEffect(() => {
    api.get("/users")
      .then((res) => setUsers((res.data || []).filter((u) => u.role !== "superadmin")))
      .catch(() => {});
  }, []);

  const handleUserChange = (val) => {
    setUserId(val);
    const selected = users.find((u) => u._id === val);
    setSendTo(selected ? selected.email : "");
  };

  // ── Guard: only Manager (admin) / Super Admin can access ──
  if (currentRole !== "admin" && currentRole !== "superadmin") {
    return (
      <div className="rd-access-denied">
        <AlertCircle size={48} color="#EF4444" />
        <h2>Access Denied</h2>
        <p>You do not have permission to view this page.</p>
      </div>
    );
  }

  const handleSend = async () => {
    const { from, to } = getRange(period, customFrom, customTo);
    if (!from || !to) {
      alert("Please select a valid date range");
      return;
    }

    const periodLabel = PERIOD_OPTIONS.find((p) => p.value === period)?.label || "Custom Range";

    try {
      setSending(true);
      const { data } = await api.post("/reports/send-mis", {
        period: periodLabel,
        from,
        to,
        recipients: sendTo,
        cc,
        userId: userId || undefined,
      });
      alert(`Report sent to ${data.to}${data.cc ? ` (CC: ${data.cc})` : ""}`);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to send report");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mis-page">
      {/* HEADER */}
      <div className="mis-topbar">
        <div className="mis-heading">
          <div className="mis-icon-wrap"><Mail size={22} /></div>
          <div>
            <h1>Reports & Digest</h1>
            <p>Send the MIS report by email to any recipients</p>
          </div>
        </div>
      </div>

      {/* SEND FORM */}
      <div className="mis-filters-bar rd-form-bar">
        <div className="mis-filter-group">
          <UserIcon size={13} />
          <select value={userId} onChange={(e) => handleUserChange(e.target.value)}>
            <option value="">All Agents</option>
            {users.map((u) => (
              <option key={u._id} value={u._id}>{u.Name || u.email}</option>
            ))}
          </select>
        </div>

        <div className="mis-filter-group">
          <select value={period} onChange={(e) => setPeriod(e.target.value)}>
            {PERIOD_OPTIONS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>

        {period === "custom" && (
          <div className="mis-custom-range">
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
            <span>→</span>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
          </div>
        )}

        <input
          className="rd-input"
          type="text"
          placeholder="Send to (optional, comma-separated)"
          value={sendTo}
          onChange={(e) => setSendTo(e.target.value)}
        />

        <input
          className="rd-input"
          type="text"
          placeholder="CC (optional, comma-separated)"
          value={cc}
          onChange={(e) => setCc(e.target.value)}
        />

        <button className="rd-btn-send" onClick={handleSend} disabled={sending}>
          <Send size={15} /> {sending ? "Sending..." : "Send Report"}
        </button>
      </div>
    </div>
  );
}
